/**
 * Retention schedule (R1-45, PRD ENT-01): how long each class of record is kept before it may be
 * deleted. The durations need legal and merchant approval (PDP, tax record-keeping) and are not
 * invented here, so every class is `null`: kept indefinitely. Nothing deletes records on a schedule
 * yet. A purge job, when one is approved, reads this policy, gives authorized owners notice and a
 * read/export window first, and never runs on deactivation (deactivation only switches things off).
 */

/** The classes of record a retention schedule distinguishes. */
export const RETENTION_CLASSES = [
  /** Orders, lines, payments, receipts, refunds: financial history (PRD invariant 7). */
  "sales",
  /** Shifts, cash movements and close approvals. */
  "cash",
  /** Stock movements, receipts and counts. */
  "stock",
  /** Offline sync commands and their exceptions: the evidence behind offline sales. */
  "sync",
  /** The audit trail. */
  "audit",
  /** Stored CSV exports (the files, not their `report_exports` records). */
  "exports",
  /** Customer contact details: subject to PDP access, correction and deletion requests. */
  "customers",
] as const;
export type RetentionClass = (typeof RETENTION_CLASSES)[number];

export interface RetentionRule {
  /** Whole days to keep a record from its business date; null keeps it indefinitely. */
  retainForDays: number | null;
  /** Where the duration stands: every rule is pending until legal and the merchant approve it. */
  status: "pending_legal_approval" | "approved";
}

/** The schedule in force: everything kept until durations are approved. */
export const RETENTION_POLICY: Readonly<Record<RetentionClass, RetentionRule>> = Object.freeze(
  Object.fromEntries(
    RETENTION_CLASSES.map((c) => [c, { retainForDays: null, status: "pending_legal_approval" }]),
  ) as Record<RetentionClass, RetentionRule>,
);
